/**
 * AllocationDetailDrawer — slide-out panel showing allocation metadata,
 * current-period usage chart, revoke + edit actions, and the
 * Decision History surface (WQ-235 activated).
 *
 * WQ-230 (Phase 4.9 α4) + WQ-235 (Phase 4.9 α9 — decision history button
 * activated).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllocationUsage as defaultGetAllocationUsage,
  revokeAllocation,
  type Allocation,
  type UsageAggregate,
} from '../../services/allocationAdminService';
import AllocationGrantModal from './AllocationGrantModal';
import DecisionHistoryDrawer from './DecisionHistoryDrawer';

/** Subset of the allocation service surface used by the drawer's read paths. */
export interface AllocationDetailReadService {
  getAllocationUsage: typeof defaultGetAllocationUsage;
}

interface AllocationDetailDrawerProps {
  allocation: Allocation | null;
  /** Operator email for revoked_by default */
  currentUserEmail?: string;
  /** WQ-231 α5: when true, hides Revoke + Edit Quota; metadata + usage chart
   * still render. */
  readOnly?: boolean;
  /** WQ-231 α5: alternate service for getAllocationUsage. Tenants pass
   * `consumptionService`; operators leave undefined. */
  serviceOverride?: AllocationDetailReadService;
  onClose: (didCommit: boolean) => void;
}

const POLL_INTERVAL_MS = 30_000;

const formatTs = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'active'
      ? 'badge badge-success'
      : status === 'revoked'
      ? 'badge badge-warning'
      : status === 'expired'
      ? 'badge badge-secondary'
      : 'badge badge-info';
  return <span className={cls}>{status}</span>;
};

const QuotaBar: React.FC<{ used: number; quota: number | null }> = ({ used, quota }) => {
  if (quota == null || quota <= 0) {
    return (
      <div data-testid="quota-bar-unlimited" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
        {used.toLocaleString()} used (no quota set)
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const color =
    pct >= 100 ? 'var(--danger, #dc2626)' : pct >= 80 ? 'var(--warning, #eab308)' : 'var(--success, #22c55e)';
  return (
    <div data-testid="quota-bar">
      <div
        style={{
          height: '12px',
          background: 'rgba(0,0,0,0.1)',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div
          data-testid="quota-bar-fill"
          style={{
            height: '100%',
            width: `${pct}%`,
            background: color,
            transition: 'width 0.2s ease',
          }}
        />
      </div>
      <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--text-secondary)' }}>
        {used.toLocaleString()} / {quota.toLocaleString()} ({pct}%)
      </div>
    </div>
  );
};

const AllocationDetailDrawer: React.FC<AllocationDetailDrawerProps> = ({
  allocation,
  currentUserEmail = '',
  readOnly = false,
  serviceOverride,
  onClose,
}) => {
  const { authFetch } = useAuth();
  const getAllocationUsage = serviceOverride?.getAllocationUsage ?? defaultGetAllocationUsage;
  const [usage, setUsage] = useState<UsageAggregate | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeBy, setRevokeBy] = useState('');
  const [revokeNotes, setRevokeNotes] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchUsage = useCallback(async () => {
    if (!allocation) return;
    setUsageLoading(true);
    setUsageError(null);
    try {
      const u = await getAllocationUsage(authFetch, allocation.id);
      setUsage(u);
    } catch (err) {
      // 404 is normal when there's no current-period usage row yet
      const msg = err instanceof Error ? err.message : 'usage fetch failed';
      if (!/404/.test(msg)) setUsageError(msg);
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  }, [authFetch, allocation]);

  useEffect(() => {
    if (!allocation) return;
    setRevokeBy(currentUserEmail);
    setRevokeNotes('');
    setActionError(null);
    setConfirmingRevoke(false);
    fetchUsage();
    const interval = setInterval(fetchUsage, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [allocation, currentUserEmail, fetchUsage]);

  if (!allocation) return null;

  const handleRevoke = async () => {
    if (!revokeBy) {
      setActionError('revoked_by is required.');
      return;
    }
    setRevoking(true);
    setActionError(null);
    try {
      await revokeAllocation(authFetch, allocation.id, {
        revoked_by: revokeBy,
        notes: revokeNotes || undefined,
      });
      onClose(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Revoke failed');
      setRevoking(false);
    }
  };

  const handleEditClose = (didCommit: boolean) => {
    setEditOpen(false);
    if (didCommit) {
      // Refresh usage in case quota changed; signal parent to refresh list
      fetchUsage();
      onClose(true);
    }
  };

  const isActive = allocation.status === 'active';
  const isCustomTier = allocation.tier_preset === 'custom';

  // counter_value lives on the Allocation row; current-period UsageAggregate
  // gives the breakdown if present.
  const usedNow = usage?.counter_value ?? allocation.current_used ?? 0;

  return (
    <div
      className="drawer-overlay"
      onClick={() => onClose(false)}
      data-testid="allocation-detail-drawer"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
      }}
    >
      <div
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '480px',
          maxWidth: '90vw',
          background: 'var(--background, #fff)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.2)',
          overflow: 'auto',
          padding: '20px 24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>
            {allocation.capability_type}
            {allocation.capability_id ? (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                {allocation.capability_id.slice(0, 8)}…
              </span>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                (wildcard)
              </span>
            )}
          </h3>
          <button className="btn btn-ghost" onClick={() => onClose(false)} aria-label="Close">
            {'\u00d7'}
          </button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <StatusBadge status={allocation.status} />
        </div>

        <section style={{ marginBottom: '20px' }}>
          <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Metadata</h4>
          <div className="detail-row"><strong>Tenant:</strong> {allocation.tenant_id}</div>
          <div className="detail-row"><strong>Tier preset:</strong> {allocation.tier_preset || '—'}</div>
          <div className="detail-row">
            <strong>Quota:</strong>{' '}
            {allocation.quota_units != null
              ? `${allocation.quota_units.toLocaleString()} / ${allocation.quota_period}`
              : 'unlimited'}
          </div>
          <div className="detail-row"><strong>Granted by:</strong> {allocation.granted_by || '—'}</div>
          <div className="detail-row"><strong>Granted at:</strong> {formatTs(allocation.granted_at)}</div>
          {allocation.revoked_at && (
            <>
              <div className="detail-row"><strong>Revoked by:</strong> {allocation.revoked_by || '—'}</div>
              <div className="detail-row"><strong>Revoked at:</strong> {formatTs(allocation.revoked_at)}</div>
            </>
          )}
          {allocation.expires_at && (
            <div className="detail-row"><strong>Expires at:</strong> {formatTs(allocation.expires_at)}</div>
          )}
          {allocation.notes && (
            <div className="detail-row"><strong>Notes:</strong> {allocation.notes}</div>
          )}
        </section>

        <section style={{ marginBottom: '20px' }}>
          <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Current Period Usage</h4>
          {usageLoading && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>loading…</div>}
          {usageError && (
            <div role="alert" style={{ fontSize: '12px', color: 'var(--danger, #dc2626)' }}>
              {usageError}
            </div>
          )}
          <QuotaBar used={usedNow} quota={allocation.quota_units} />
          {usage && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Period started {formatTs(usage.period_start_at)}
            </div>
          )}
        </section>

        {actionError && (
          <div role="alert" style={{
            padding: '8px 12px',
            background: 'rgba(220,38,38,0.1)',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: '6px',
            color: 'var(--danger, #dc2626)',
            fontSize: '13px',
            marginBottom: '12px',
          }}>
            {actionError}
          </div>
        )}

        {!readOnly && isActive && !confirmingRevoke && (
          <section style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button
              className="btn btn-warning"
              onClick={() => setConfirmingRevoke(true)}
              data-testid="revoke-button"
            >
              Revoke
            </button>
            {isCustomTier && (
              <button
                className="btn btn-secondary"
                onClick={() => setEditOpen(true)}
                data-testid="edit-quota-button"
              >
                Edit Quota
              </button>
            )}
          </section>
        )}

        {!readOnly && confirmingRevoke && (
          <section
            data-testid="revoke-confirm-panel"
            style={{
              padding: '12px',
              background: 'rgba(234,179,8,0.1)',
              border: '1px solid rgba(234,179,8,0.4)',
              borderRadius: '6px',
              marginBottom: '20px',
              display: 'grid',
              gap: '8px',
            }}
          >
            <div style={{ fontWeight: 600 }}>Confirm revoke</div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px' }}>Revoked by</span>
              <input
                value={revokeBy}
                onChange={(e) => setRevokeBy(e.target.value)}
                placeholder="ops@example.com"
                aria-label="Revoked By"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px' }}>Notes (optional)</span>
              <textarea
                value={revokeNotes}
                onChange={(e) => setRevokeNotes(e.target.value)}
                rows={2}
                aria-label="Revoke Notes"
              />
            </label>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setConfirmingRevoke(false)}
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={handleRevoke}
                disabled={revoking}
                data-testid="confirm-revoke"
              >
                {revoking ? 'Revoking…' : 'Confirm Revoke'}
              </button>
            </div>
          </section>
        )}

        <section style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '12px' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setHistoryOpen(true)}
            data-testid="decision-history-button"
            title="Open SQA decision history filtered to this tenant + capability"
            style={{ fontSize: '12px' }}
          >
            View Decision History →
          </button>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Pre-filtered to this tenant + capability. WQ-235.
          </div>
        </section>

        {historyOpen && (
          <DecisionHistoryDrawer
            isOpen={historyOpen}
            onClose={() => setHistoryOpen(false)}
            preFilteredTenantId={allocation.tenant_id}
            preFilteredCapabilityType={allocation.capability_type}
          />
        )}

        {!readOnly && editOpen && (
          <AllocationGrantModal
            isOpen={editOpen}
            mode="edit"
            scopedTenantId={allocation.tenant_id}
            editing={allocation}
            currentUserEmail={currentUserEmail}
            onClose={handleEditClose}
          />
        )}
      </div>
    </div>
  );
};

export default AllocationDetailDrawer;
